import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import { api } from '../lib/api'
import { DEFAULT_ARTEFACT_TYPE, type ArtefactType } from '../lib/artefact-type'
import { useBetaTester } from '../lib/beta'
import {
  buildTemplateTree,
  categoryOptions,
  isTreeEmpty,
  type TemplateEntry,
  type TemplateNode,
} from '../lib/template-catalogue'
import { ArtefactTypeGlyph } from './ArtefactTypeIcon'

const TYPE_LABEL: Record<ArtefactType, Key> = {
  prose: 'artefact_type_prose',
  slides: 'artefact_type_slides',
  grid: 'artefact_type_grid',
}
const TYPE_HINT: Record<ArtefactType, Key> = {
  prose: 'artefact_type_prose_hint',
  slides: 'artefact_type_slides_hint',
  grid: 'artefact_type_grid_hint',
}

/**
 * "New artefact" — its type, its name and, optionally, the library template it copies (AB#270).
 *
 * The type (AB#197) is fixed for the life of the artefact (lib/artefact-type.ts), so it is asked
 * here, as a row of icon-only radios — the explorer's own glyphs (ArtefactTypeIcon), the type name
 * as tooltip and accessible name, its "exports to…" line as the description. Prose is preselected.
 * Grid (AB#263) is offered to beta testers only while grid parts are beta (lib/beta.ts) — the
 * server refuses it from anyone else anyway, grid templates included, so those are not listed.
 *
 * "From template" (a role="switch") widens the dialog onto the template library's catalogue
 * (GET /v1/artefact-templates): a search over name and description, a category filter and the
 * categories/templates tree drawn with the explorer's own row styles. Picking a template shows its
 * description, sets the type to the template's and locks it (the copy keeps the template's type),
 * and pre-fills the name while it is empty or still the previous template's name. Switching off
 * clears the pick and unlocks the type. On a narrow screen the panel stacks below instead.
 *
 * "From PowerPoint" (AB#442, with `onFromPowerPoint`), the other switch — the two exclude each
 * other — makes it a slides artefact (the type locked) and turns Create into Continue: the caller
 * then opens the PowerPoint import, which creates the artefact once the deck and its slides are
 * chosen (components/ImportPowerPointDialog.tsx#NewDeckFromPowerPoint).
 *
 * Busy handling is PromptModal's (AB#229): a returned promise keeps the dialog busy — no second
 * submit, no close — until it settles; a rejection comes back inline for a retry.
 *
 * Shared by the two places a deliverable is created: the explorer (LeftNav) and the bid home.
 */
export function NewArtefactModal({
  onSubmit,
  onFromPowerPoint,
  onClose,
}: {
  /**
   * `templateId` is the picked library template, `null` for a blank artefact. Return the creation
   * promise: the dialog stays busy (no second submit) until it settles.
   */
  onSubmit: (title: string, type: ArtefactType, templateId: string | null) => Promise<unknown>
  /** Offers "From PowerPoint": Continue hands the name over, the caller imports a deck into it. */
  onFromPowerPoint?: (title: string) => void
  onClose: () => void
}) {
  const { t } = useLang()
  const { isBeta } = useBetaTester()
  const types: ArtefactType[] = isBeta ? ['prose', 'slides', 'grid'] : ['prose', 'slides']
  const [type, setType] = useState<ArtefactType>(DEFAULT_ARTEFACT_TYPE)
  const [name, setName] = useState('')
  const [fromTemplate, setFromTemplate] = useState(false)
  const [fromPptx, setFromPptx] = useState(false)
  const [picked, setPicked] = useState<TemplateEntry | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ids = useId()
  const inputRef = useRef<HTMLInputElement | null>(null)
  // The synchronous guard: React state alone lets a second click land before the re-render.
  const busyRef = useRef(false)
  // The caller usually unmounts the dialog on success before its promise settles — no state then.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    inputRef.current?.focus()
    return () => prev?.focus()
  }, [])

  const locked = picked !== null || fromPptx
  const shownType = fromPptx ? 'slides' : (picked?.type ?? type)

  const pick = (tpl: TemplateEntry): void => {
    setPicked(tpl)
    if (!name.trim() || name === picked?.name) setName(tpl.name)
  }
  const toggleTemplate = (): void => {
    if (fromTemplate) setPicked(null)
    else setFromPptx(false)
    setFromTemplate(!fromTemplate)
  }
  const togglePptx = (): void => {
    if (!fromPptx) {
      setFromTemplate(false)
      setPicked(null)
    }
    setFromPptx(!fromPptx)
  }

  const canSubmit = Boolean(name.trim()) && (!fromTemplate || picked !== null)
  const submit = (): void => {
    if (busyRef.current || !canSubmit) return
    if (fromPptx && onFromPowerPoint) {
      onFromPowerPoint(name.trim())
      return
    }
    busyRef.current = true
    setBusy(true)
    setError(null)
    onSubmit(name.trim(), shownType, picked?.id ?? null).then(
      () => {
        busyRef.current = false
        if (mountedRef.current) setBusy(false)
      },
      (e: unknown) => {
        busyRef.current = false
        if (!mountedRef.current) return
        setBusy(false)
        const server = (e as { serverMessage?: unknown } | null)?.serverMessage
        setError(typeof server === 'string' && server ? server : t('prompt_failed'))
      },
    )
  }
  const close = (): void => {
    if (!busyRef.current) onClose()
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={close}>
      <div
        className={'modal prompt-modal newart-modal' + (fromTemplate ? ' wide' : '')}
        role="dialog"
        aria-modal="true"
        aria-label={t('new_artefact_title')}
        aria-busy={busy || undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close()
        }}
      >
        <div className="modal-head">
          <h2>{t('new_artefact_title')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={close} disabled={busy}>✕</button>
        </div>
        <div className="modal-body prompt-body">
          <div className="newart-grid">
            <div className="newart-main">
              <fieldset className="newart-types" disabled={busy || locked}>
                <legend className="prompt-label">{t('artefact_type_lbl')}</legend>
                <div className="newart-type-row">
                  {types.map((ty) => (
                    // The tile is the radio's label: the glyph is decorative, the radio carries the
                    // type name (aria-label) and its "exports to…" line (aria-describedby).
                    <label key={ty} className="newart-type" title={t(TYPE_LABEL[ty])}>
                      <input
                        type="radio"
                        name={`${ids}-type`}
                        value={ty}
                        aria-label={t(TYPE_LABEL[ty])}
                        aria-describedby={`${ids}-hint-${ty}`}
                        checked={shownType === ty}
                        onChange={() => setType(ty)}
                      />
                      <span className="newart-type-glyph" aria-hidden="true"><ArtefactTypeGlyph type={ty} /></span>
                      <span id={`${ids}-hint-${ty}`} className="visually-hidden">{t(TYPE_HINT[ty])}</span>
                    </label>
                  ))}
                </div>
                {/* The checked type's line, for sighted users (AT gets it as each radio's description). */}
                <p className="newart-type-hint" aria-hidden="true">
                  <b>{t(TYPE_LABEL[shownType])}</b> — {t(TYPE_HINT[shownType])}
                </p>
                {locked && <p className="newart-type-hint">{t(fromPptx ? 'pptx_type_locked' : 'tpl_type_locked')}</p>}
              </fieldset>
              <label className="prompt-label">
                {t('artefact_name')}
                <input
                  ref={inputRef}
                  className="prompt-input"
                  value={name}
                  disabled={busy}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      submit()
                    }
                  }}
                />
              </label>
              <div className="newart-switch-row">
                <span id={`${ids}-switch`}>{t('tpl_from_template')}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={fromTemplate}
                  aria-labelledby={`${ids}-switch`}
                  className={'switch' + (fromTemplate ? ' on' : '')}
                  disabled={busy}
                  onClick={toggleTemplate}
                >
                  <span className="switch-knob" aria-hidden="true" />
                </button>
              </div>
              {onFromPowerPoint && (
                <div className="newart-switch-row">
                  <span id={`${ids}-pptx`}>{t('pptx_from_pptx')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={fromPptx}
                    aria-labelledby={`${ids}-pptx`}
                    className={'switch' + (fromPptx ? ' on' : '')}
                    disabled={busy}
                    onClick={togglePptx}
                  >
                    <span className="switch-knob" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
            {fromTemplate && <TemplatePanel picked={picked} onPick={pick} disabled={busy} gridAllowed={isBeta} />}
          </div>
          {error && <p className="prompt-error" role="alert">{error}</p>}
          <p className="visually-hidden" role="status">{busy ? t('creating') : ''}</p>
          <div className="prompt-acts">
            <button className="mini ghost" onClick={close} disabled={busy}>{t('cancel')}</button>
            <button className="mini ok prompt-confirm" onClick={submit} disabled={busy || !canSubmit}>
              {busy && <span className="brand-spinner" aria-hidden="true" />}
              {t(fromPptx ? 'pptx_continue' : 'create')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The template side of the dialog: search, category filter, the tree, the picked template's
 * description. The tree reuses the artefact explorer's rows (.folder / .item.folder-row /
 * .folder-toggle / .folder-children, the same glyphs) so the library reads exactly like a bid's
 * explorer; every row is a native button, so Tab / Enter / Space navigate it as they do there.
 */
function TemplatePanel({
  picked,
  onPick,
  disabled,
  gridAllowed,
}: {
  picked: TemplateEntry | null
  onPick: (tpl: TemplateEntry) => void
  disabled: boolean
  /** Outside the grid beta a grid template cannot be instantiated (the server 403s) — not listed. */
  gridAllowed: boolean
}) {
  const { t } = useLang()
  const [query, setQuery] = useState('')
  const [categoryId, setCategoryId] = useState('')
  // Explicit expand/collapse per category; absent = open, like the explorer's folders.
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const catalogue = useQuery({ queryKey: ['artefact-templates'], queryFn: () => api.artefactTemplates() })

  const visible = useMemo(() => {
    const data = catalogue.data ?? { categories: [], templates: [] }
    return { ...data, templates: data.templates.filter((tpl) => gridAllowed || tpl.type !== 'grid') }
  }, [catalogue.data, gridAllowed])
  const filtering = query.trim() !== '' || categoryId !== ''
  const tree = useMemo(() => buildTemplateTree(visible, { query, categoryId: categoryId || null }), [visible, query, categoryId])
  const options = useMemo(() => categoryOptions(visible.categories), [visible.categories])
  const catalogueEmpty = visible.templates.length === 0 && visible.categories.length === 0

  function renderTemplate(tpl: TemplateEntry) {
    const selected = picked?.id === tpl.id
    return (
      <button
        key={tpl.id}
        type="button"
        className={'item tpl-row' + (selected ? ' selected' : '')}
        aria-pressed={selected}
        disabled={disabled}
        onClick={() => onPick(tpl)}
      >
        <span className="twisty" aria-hidden="true" />
        <span className="gi" title={t(TYPE_LABEL[tpl.type])}>
          <span aria-hidden="true"><ArtefactTypeGlyph type={tpl.type} /></span>
          <span className="visually-hidden">{t(TYPE_LABEL[tpl.type])} — </span>
        </span>
        <span className="lbl">{tpl.name}</span>
      </button>
    )
  }

  function renderCategory(node: TemplateNode) {
    const { category } = node
    // A search shows its matches expanded — collapsed hits would be a list of empty folders.
    const expanded = filtering || (open[category.id] ?? true)
    const count = node.children.length + node.templates.length
    return (
      <div key={category.id} className="folder">
        <div className="item folder-row">
          <button
            type="button"
            className="folder-toggle"
            aria-expanded={expanded}
            onClick={() => setOpen((prev) => ({ ...prev, [category.id]: !expanded }))}
          >
            <span className={'twisty' + (expanded ? ' open' : '')} aria-hidden="true">▸</span>
            <span className="gi">{expanded ? '📂' : '📁'}</span>
            <span className="lbl">{category.title}</span>
            <span className="ct">{count}</span>
          </button>
        </div>
        {expanded && (
          <div className="folder-children">
            {node.children.map(renderCategory)}
            {node.templates.map(renderTemplate)}
            {count === 0 && <div className="folder-empty" aria-disabled="true">{t('folder_empty')}</div>}
          </div>
        )}
      </div>
    )
  }

  return (
    <section className="newart-tpl" aria-label={t('tpl_panel')}>
      {catalogue.isPending ? (
        <p className="newart-tpl-note" role="status">{t('loading')}</p>
      ) : catalogue.isError ? (
        <p className="prompt-error" role="alert">{t('tpl_load_failed')}</p>
      ) : catalogueEmpty ? (
        <p className="newart-tpl-note">{t('tpl_empty')}</p>
      ) : (
        <>
          <div className="newart-tpl-filters">
            <input
              type="search"
              className="prompt-input"
              aria-label={t('tpl_search')}
              placeholder={t('tpl_search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <label className="prompt-label">
              {t('tpl_category')}
              <select className="prompt-input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">{t('tpl_all_categories')}</option>
                {options.map(({ category, depth }) => (
                  <option key={category.id} value={category.id}>
                    {'  '.repeat(depth - 1) + (depth > 1 ? '└ ' : '') + category.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="newart-tree" aria-label={t('tpl_panel')} role="group">
            {isTreeEmpty(tree) ? (
              <p className="newart-tpl-note">{t('tpl_no_match')}</p>
            ) : (
              <>
                {tree.roots.map(renderCategory)}
                {tree.rootTemplates.map(renderTemplate)}
              </>
            )}
          </div>
          <div className="newart-tpl-desc" aria-live="polite">
            {picked ? (
              <>
                <b>{picked.name}</b>
                <p>{picked.description || t('tpl_no_description')}</p>
              </>
            ) : (
              <p className="newart-tpl-note">{t('tpl_pick')}</p>
            )}
          </div>
        </>
      )}
    </section>
  )
}
